import styles from './Pill.module.css'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type PillType = 'Circle' | 'Rectangle' | 'Active' | 'Inactive' | 'Category' | 'Status'
export type PillSize = 'Medium' | 'Small' | 'XSmall'

export type PillProps = {
  /** 'Circle': 円形バッジ / 'Rectangle': ピル型ラベル（デフォルト: 'Circle'） */
  type?: PillType
  /**
   * Circle 時のサイズ（デフォルト: 'Medium'）
   * Medium=20px / Small=16px / XSmall=8px
   */
  size?: PillSize
  /** Circle+Medium 時のみ有効。true にすると数字テキストを表示する */
  showNumber?: boolean
  /** showNumber=true のときに表示するテキスト（デフォルト: 'N'） */
  numberText?: string
  /** Rectangle 時のラベルテキスト */
  label?: string
  className?: string
}

// ─────────────────────────────────────────────
// サイズマップ（Circle）
// ─────────────────────────────────────────────
const CIRCLE_SIZE_CLASS: Record<PillSize, string> = {
  Medium: styles.circleMedium,
  Small: styles.circleSmall,
  XSmall: styles.circleXSmall,
}

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
/**
 * 通知・状態表示に使う汎用ピル。
 * Circle（円形ドット）と Rectangle（ピル型ラベル）の 2 種類。
 *
 * @example
 * <Pill type="Circle" size="Medium" />
 * <Pill type="Circle" size="Medium" showNumber numberText="3" />
 * <Pill type="Rectangle" label="NEW" />
 */
export const Pill = ({
  type = 'Circle',
  size = 'Medium',
  showNumber = false,
  numberText = 'N',
  label,
  className,
}: PillProps) => {
  if (type === 'Category') {
    return (
      <div className={[styles.category, className ?? ''].join(' ').trim()}>
        <span className={`${styles.label} ${styles.categoryLabel}`}>{label}</span>
      </div>
    )
  }

  if (type === 'Rectangle') {
    return (
      <div className={[styles.rectangle, className ?? ''].join(' ').trim()}>
        <span className={`${styles.label} ${styles.rectangleLabel}`}>{label}</span>
      </div>
    )
  }

  if (type === 'Active') {
    return (
      <div className={[styles.active, className ?? ''].join(' ').trim()}>
        <span className={`${styles.label} ${styles.activeLabel}`}>{label}</span>
      </div>
    )
  }

  if (type === 'Inactive') {
    return (
      <div className={[styles.inactive, className ?? ''].join(' ').trim()}>
        <span className={`${styles.label} ${styles.inactiveLabel}`}>{label}</span>
      </div>
    )
  }

  if (type === 'Status') {
    return (
      <div className={[styles.status, className ?? ''].join(' ').trim()}>
        <span className={`${styles.label} ${styles.statusLabel}`}>{label}</span>
      </div>
    )
  }

  // Circle
  const sizeClass = CIRCLE_SIZE_CLASS[size]
  return (
    <div className={[styles.circle, sizeClass, className ?? ''].join(' ').trim()}>
      {showNumber && size === 'Medium' && (
        <span className={`${styles.label} ${styles.circleNumber}`}>{numberText}</span>
      )}
    </div>
  )
}
